import React, { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { Project } from '../../firebase/projectService';
import { projectQuery, projectsQuery } from '../../queries/contentQueries';
import { ProjectDetailSkeleton } from '../../components/skeletons/PageSkeletons';
import { CheckBadgeIcon } from '@heroicons/react/24/outline';

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: project = null, isPending: isLoading, error } = useQuery({
    ...projectQuery(id || ''),
    // Coming from the projects list, show the cached project immediately instead of refetching
    initialData: (): Project | undefined =>
      queryClient.getQueryData(projectsQuery.queryKey)?.find((p) => p.id === id),
    initialDataUpdatedAt: () => queryClient.getQueryState(projectsQuery.queryKey)?.dataUpdatedAt,
  });

  useEffect(() => {
    if (!isLoading && !project) {
      // Wait a bit before redirecting to projects page
      const timer = setTimeout(() => {
        navigate('/projects');
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, project, navigate]);

  if (isLoading) {
    return <ProjectDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="container py-16 lg:pt-[var(--page-top)]">
        <div className="bg-red-100 border border-red-400 text-red-700 p-6 rounded-lg text-center">
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <p>{error instanceof Error ? error.message : 'Failed to load project data'}</p>
          <p className="mt-4">Redirecting to projects page...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container py-16 lg:pt-[var(--page-top)]">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 p-6 rounded-lg text-center">
          <h2 className="text-xl font-bold mb-2">Project Not Found</h2>
          <p>The project you're looking for doesn't exist or has been removed.</p>
          <p className="mt-4">Redirecting to projects page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-16 lg:pt-[var(--page-top)]">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero section */}
        <div className="mb-12">
          <h1 className="section-title">
            {project.title}
            {project.featured && (
              <span className="inline-flex items-center ml-3 text-yellow-500" title="Featured Project">
                <Star className="mr-1" size={20} />
                <span className="text-base font-medium">Featured</span>
              </span>
            )}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mt-4">
            {project.description}
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {project.tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-md text-sm font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Main image */}
        <div className="mb-12 rounded-lg overflow-hidden shadow-lg">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Project details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold mb-4">About the Project</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 whitespace-pre-line">
              {project.longDescription}
            </p>
          </div>

          <div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-6">
              <h2 className="text-2xl font-bold mb-4">Key Features</h2>
              {project.features && project.features.length > 0 ? (
                <div className="space-y-4">
                  {project.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="border-l-4 border-primary-500 pl-4">
                      <h3 className="font-semibold text-primary-600 dark:text-primary-400 mb-2">
                        {feature.name}
                      </h3>
                      <ul className="space-y-1">
                        {feature.subFeatures.map((subFeature, subIndex) => (
                          <li key={subIndex} className="flex items-start space-x-2">
                            <CheckBadgeIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-300 text-sm">{subFeature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">No features listed for this project.</p>
              )}
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-bold mb-4">Technologies Used</h2>
              <ul className="space-y-2">
                {project.technologies.map((tech, index) => (
                  <li key={index} className="text-gray-600 dark:text-gray-400">
                    • {tech}
                  </li>
                ))}
              </ul>

              {(project.link || project.github) && (
                <div className="mt-8 space-y-3">
                  <h3 className="text-lg font-semibold">Links</h3>
                  {project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full py-2 px-4 bg-primary-600 text-white text-center rounded-md hover:bg-primary-700 transition-colors"
                    >
                      View Live Project
                    </a>
                  )}
                  {project.github && (
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full py-2 px-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white text-center rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      View Source Code
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Gallery */}
        {project.gallery && project.gallery.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Project Gallery</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {project.gallery.map((image, index) => (
                <div key={index} className="rounded-lg overflow-hidden shadow-md">
                  <img
                    src={image}
                    alt={`${project.title} screenshot ${index + 1}`}
                    className="w-full h-auto object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ProjectDetailPage; 